import React from 'react';
import {
  TrendingDown,
  TrendingUp,
  DollarSign,
  AlertTriangle,
  Lightbulb,
  Target,
  ShieldCheck,
} from 'lucide-react';

export const AboutUs: React.FC = () => {
  const questions = [
    { text: 'Are we actually making money?', icon: DollarSign, color: 'var(--signal-green)' },
    { text: 'Where are we losing money?', icon: TrendingDown, color: 'var(--brand-red)' },
    { text: 'Which customers, products and services are most profitable?', icon: TrendingUp, color: 'var(--signal-blue)' },
    { text: 'Why is cash flow under pressure?', icon: AlertTriangle, color: 'var(--signal-amber)' },
    { text: 'Where are costs increasing?', icon: TrendingDown, color: '#ff6b6b' },
    { text: 'What risks require attention?', icon: AlertTriangle, color: 'var(--brand-red-light)' },
    { text: 'What opportunities should we pursue next?', icon: Lightbulb, color: 'var(--signal-amber)' },
    { text: 'What actions will improve business performance?', icon: Target, color: 'var(--signal-green)' },
  ];

  return (
    <div style={{ paddingTop: '80px' }}>
      {/* Header */}
      <section
        style={{
          paddingTop: '64px',
          paddingBottom: '80px',
          borderBottom: '1px solid var(--border-subtle)',
          background: 'radial-gradient(ellipse 70% 50% at 50% 0%, var(--brand-red-glow-subtle), transparent)',
        }}
      >
        <div className="container-max" style={{ textAlign: 'center', maxWidth: '820px' }}>
          <span className="badge-pill badge-red" style={{ marginBottom: '16px' }}>
            ABOUT FORTY2
          </span>
          <h1
            style={{
              fontSize: 'clamp(2.5rem, 5vw, 3.8rem)',
              fontWeight: 800,
              color: 'var(--text-primary)',
              letterSpacing: '-0.02em',
              marginBottom: '20px',
            }}
          >
            Beyond Accounting
          </h1>
          <p
            className="font-serif"
            style={{
              fontSize: '1.45rem',
              fontStyle: 'italic',
              color: 'var(--brand-red-light)',
              lineHeight: '1.5',
            }}
          >
            Enabling structured visibility, cash flow clarity, and smarter business decisions.
          </p>
        </div>
      </section>

      {/* Origin Story & Pedigree */}
      <section style={{ padding: '96px 0' }}>
        <div className="container-max">
          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
              gap: '48px',
              alignItems: 'center',
            }}
          >
            <div>
              <span className="badge-pill badge-neutral font-mono" style={{ marginBottom: '14px' }}>
                FOUNDATION & ETHOS
              </span>
              <h2
                style={{
                  fontSize: 'clamp(1.8rem, 3.5vw, 2.5rem)',
                  fontWeight: 800,
                  color: 'var(--text-primary)',
                  marginBottom: '20px',
                  lineHeight: '1.25',
                }}
              >
                Second-Generation Rigor Meets Modern Digital Execution
              </h2>
              <p style={{ fontSize: '1.05rem', color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '18px' }}>
                Forty2 was founded by a team consisting of <strong style={{ color: 'var(--text-primary)' }}>second-generation Chartered Accountants</strong> along with experienced professionals from startup environments, finance operations, digital workflows, and cross-border business process delivery.
              </p>
              <p style={{ fontSize: '1.05rem', color: 'var(--text-secondary)', lineHeight: '1.7', marginBottom: '18px' }}>
                The firm was built on the belief that growing businesses need more than accounting and compliance — they need structured financial visibility, cash flow clarity, and decision support.
              </p>
              <p style={{ fontSize: '1.05rem', color: 'var(--text-secondary)', lineHeight: '1.7' }}>
                Forty2 combines strong finance discipline with modern digital execution to help businesses move from accounting to financial intelligence and build organizations that are <span style={{ color: 'var(--signal-green)', fontWeight: 600 }}>predictable, scalable, and sustainable</span>.
              </p>
            </div>

            {/* Visual Accent Card */}
            <div
              className="glass-panel-elevated"
              style={{
                padding: '40px',
                border: '1px solid rgba(229, 57, 53, 0.3)',
                background: 'var(--bg-surface-elevated)',
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: '14px', marginBottom: '24px' }}>
                <div
                  style={{
                    width: '48px',
                    height: '48px',
                    borderRadius: '12px',
                    backgroundColor: 'rgba(229, 57, 53, 0.15)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    border: '1px solid rgba(229, 57, 53, 0.3)',
                  }}
                >
                  <ShieldCheck size={26} color="var(--brand-red)" />
                </div>
                <div>
                  <h4 style={{ fontSize: '1.1rem', fontWeight: 700, color: 'var(--text-primary)' }}>
                    CA Firm Certified Accountability
                  </h4>
                  <span style={{ fontSize: '0.8rem', color: 'var(--text-muted)' }}>
                    Statutory & Compliance Oversight
                  </span>
                </div>
              </div>

              <div style={{ display: 'flex', flexDirection: 'column', gap: '14px', fontSize: '0.92rem', color: 'var(--text-secondary)' }}>
                <div style={{ padding: '12px 16px', background: 'var(--border-subtle)', borderRadius: '8px', borderLeft: '3px solid var(--signal-green)' }}>
                  ✓ <strong>100% Statutory Accuracy:</strong> GST, TDS, advance tax, and regulatory compliance signed off by practicing CAs.
                </div>
                <div style={{ padding: '12px 16px', background: 'var(--border-subtle)', borderRadius: '8px', borderLeft: '3px solid var(--signal-blue)' }}>
                  ✓ <strong>Cross-Border Delivery:</strong> Seasoned delivery workflows supporting companies in India, GCC/Middle East, and Southeast Asia.
                </div>
                <div style={{ padding: '12px 16px', background: 'var(--border-subtle)', borderRadius: '8px', borderLeft: '3px solid var(--brand-red)' }}>
                  ✓ <strong>Fractional CFO Agility:</strong> Enterprise-level boardroom analytics delivered at a fraction of full-time executive cost.
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* The 8 Questions Section */}
      <section
        style={{
          padding: '96px 0',
          backgroundColor: 'var(--bg-surface)',
          borderTop: '1px solid var(--border-subtle)',
          borderBottom: '1px solid var(--border-subtle)',
        }}
      >
        <div className="container-max">
          <div style={{ textAlign: 'center', maxWidth: '780px', margin: '0 auto 56px auto' }}>
            <span className="badge-pill badge-red" style={{ marginBottom: '14px' }}>
              DECISION FRAMEWORK
            </span>
            <h2
              style={{
                fontSize: 'clamp(2rem, 3.8vw, 2.75rem)',
                fontWeight: 800,
                color: 'var(--text-primary)',
                marginBottom: '16px',
              }}
            >
              Businesses don't need more reports. They need answers.
            </h2>
            <p style={{ fontSize: '1.1rem', color: 'var(--text-secondary)' }}>
              Forty2 helps business owners and management teams answer the questions that matter most.
            </p>
          </div>

          <div
            style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(280px, 1fr))',
              gap: '18px',
            }}
          >
            {questions.map((q, idx) => {
              const Icon = q.icon;
              return (
                <div
                  key={idx}
                  className="glass-panel"
                  style={{
                    padding: '24px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '16px',
                    transition: 'transform 0.2s ease, border-color 0.2s ease',
                  }}
                >
                  <div
                    style={{
                      width: '40px',
                      height: '40px',
                      borderRadius: '10px',
                      backgroundColor: 'var(--border-subtle)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      flexShrink: 0,
                    }}
                  >
                    <Icon size={20} color={q.color} />
                  </div>
                  <span style={{ fontSize: '1rem', fontWeight: 600, color: 'var(--text-primary)' }}>
                    {q.text}
                  </span>
                </div>
              );
            })}
          </div>

          <p
            style={{
              textAlign: 'center',
              marginTop: '40px',
              fontSize: '1.05rem',
              color: 'var(--text-secondary)',
            }}
          >
            By turning financial data into actionable intelligence, Forty2 helps businesses make better decisions with clarity and confidence.
          </p>
        </div>
      </section>

      {/* The 42 & ४२ Story Section */}
      <section style={{ padding: '96px 0' }}>
        <div className="container-max" style={{ maxWidth: '880px', textAlign: 'center' }}>
          <span className="badge-pill badge-neutral font-mono" style={{ marginBottom: '16px' }}>
            THE NAME & SYMBOLISM
          </span>

          <h2
            className="font-serif"
            style={{
              fontSize: 'clamp(2.2rem, 4.5vw, 3.2rem)',
              fontStyle: 'italic',
              color: 'var(--text-primary)',
              marginBottom: '32px',
              lineHeight: '1.2',
            }}
          >
            Complex questions sometimes have elegant answers.
          </h2>

          <div
            className="glass-panel-elevated"
            style={{
              padding: '48px',
              textAlign: 'left',
              display: 'flex',
              flexDirection: 'column',
              gap: '24px',
              border: '1px solid rgba(229, 57, 53, 0.3)',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '16px',
                  background: 'linear-gradient(135deg, rgba(229, 57, 53, 0.2) 0%, rgba(198, 40, 40, 0.4) 100%)',
                  border: '1px solid var(--border-red)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--brand-red)',
                  fontSize: '1.8rem',
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                42
              </div>
              <div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  In The Hitchhiker's Guide to the Galaxy
                </h3>
                <p style={{ fontSize: '1.02rem', color: 'var(--text-secondary)', lineHeight: '1.65' }}>
                  A supercomputer calculates the Answer to the Ultimate Question of Life, the Universe, and Everything as simply <strong>42</strong>. The idea is humorous but reflects an important principle — <em>answers only matter when supported by the right data, systems, and analysis.</em>
                </p>
              </div>
            </div>

            <div style={{ width: '100%', height: '1px', background: 'var(--border-subtle)' }} />

            <div style={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
              <div
                style={{
                  width: '64px',
                  height: '64px',
                  borderRadius: '16px',
                  background: 'var(--border-subtle)',
                  border: '1px solid var(--border-subtle)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  color: 'var(--text-primary)',
                  fontSize: '2rem',
                  fontWeight: 800,
                  flexShrink: 0,
                }}
              >
                ४२
              </div>
              <div>
                <h3 style={{ fontSize: '1.3rem', fontWeight: 700, color: 'var(--text-primary)', marginBottom: '8px' }}>
                  The Devanagari Numeral ४२
                </h3>
                <p style={{ fontSize: '1.02rem', color: 'var(--text-secondary)', lineHeight: '1.65' }}>
                  Our logo also incorporates the Devanagari numeral <strong>४२</strong>, acknowledging the Indian origins of modern numerals and decimal mathematics that power global commerce and financial ledgers today.
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};
